import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCardIcon from "@mui/icons-material/AddCard";
import { API_URL } from "./config";
import {
  getCampusFromToken,
  getAuthHeaders,
} from "./commonfunctions/useAuthHelpers";

const WalletAdminPage = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(240,120,40,0.08)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 1,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      backgroundColor: "rgba(25,118,210,0.95)",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    cursor: "pointer",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const detailPaperSx = {
    borderRadius: 2,
    border: "1px solid rgba(25,118,210,0.14)",
    backgroundColor: "rgba(25,118,210,0.03)",
    p: 1.5,
  };

  const [campusCode, setCampusCode] = useState("");
  const [filters, setFilters] = useState({
    q: "",
    subCampusCode: "",
    cardType: "",
    walletStatus: "",
    page: 1,
    pageSize: 20,
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [expandedWalletId, setExpandedWalletId] = useState(null);
  const [expandedWalletDetails, setExpandedWalletDetails] = useState({});
  const [expandedWalletLedger, setExpandedWalletLedger] = useState({});
  const [loadingWalletId, setLoadingWalletId] = useState(null);

  const [selectedWalletId, setSelectedWalletId] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);

  const [recharge, setRecharge] = useState({
    amount: "",
    paymentMode: "ONLINE",
    remarks: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [payMsg, setPayMsg] = useState(null);
  const [payBusy, setPayBusy] = useState(false);

  const [options, setOptions] = useState({
    SubCampusCode: [],
  });
  const [loadingLookups, setLoadingLookups] = useState(false);

  useEffect(() => {
    setCampusCode(getCampusFromToken());
  }, []);

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);

        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          headers: getAuthHeaders(),
          params: { CampusCode: campusCode },
        });

        if (cancelled) return;

        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
        });
      } catch (e) {
        console.error(e);
        setError("Failed to load dropdowns");
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode]);

  const formatLocalDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const loadScript = (src) => {
    return new Promise((resolve) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
      const s = document.createElement("script");
      s.src = src;
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  };

  const fetchAccounts = async (overrideFilters = null) => {
    try {
      const params = overrideFilters || filters;
      const res = await axios.get(`${API_URL}/api/wallet/admin/accounts`, {
        params,
        headers: getAuthHeaders(),
      });
      setRows(res.data?.rows || []);
      setTotal(Number(res.data?.total || 0));

      if (selectedWalletId) {
        const stillExists = (res.data?.rows || []).find(
          (r) => Number(r.WalletAccountId) === Number(selectedWalletId)
        );
        if (!stillExists) {
          setSelectedWalletId(null);
          setSelectedWallet(null);
        }
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load wallet accounts");
    }
  };

  const fetchWalletDetails = async (walletAccountId) => {
    const res = await axios.get(
      `${API_URL}/api/wallet/admin/accounts/${walletAccountId}`,
      { headers: getAuthHeaders() }
    );

    const wallet = res.data?.wallet || null;
    const ledger = res.data?.ledger || [];

    setExpandedWalletDetails((prev) => ({
      ...prev,
      [walletAccountId]: wallet,
    }));

    setExpandedWalletLedger((prev) => ({
      ...prev,
      [walletAccountId]: ledger,
    }));

    if (Number(selectedWalletId) === Number(walletAccountId)) {
      setSelectedWallet(wallet);
    }

    return { wallet, ledger };
  };

  useEffect(() => {
    if (campusCode) fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((s) => ({ ...s, [name]: value }));
  };

  const handleSearch = async () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    setExpandedWalletId(null);
    await fetchAccounts(next);
  };

  const handleReset = async () => {
    const next = {
      q: "",
      subCampusCode: "",
      cardType: "",
      walletStatus: "",
      page: 1,
      pageSize: 20,
    };
    setFilters(next);
    setExpandedWalletId(null);
    setExpandedWalletDetails({});
    setExpandedWalletLedger({});
    setSelectedWalletId(null);
    setSelectedWallet(null);
    await fetchAccounts(next);
  };

  const handlePageChange = async (_e, value) => {
    const next = { ...filters, page: value };
    setFilters(next);
    setExpandedWalletId(null);
    await fetchAccounts(next);
  };

  const handlePageSizeChange = async (e) => {
    const next = {
      ...filters,
      pageSize: Number(e.target.value),
      page: 1,
    };
    setFilters(next);
    setExpandedWalletId(null);
    await fetchAccounts(next);
  };

  const toggleExpandRow = async (walletAccountId) => {
    if (Number(expandedWalletId) === Number(walletAccountId)) {
      setExpandedWalletId(null);
      return;
    }

    try {
      setLoadingWalletId(walletAccountId);
      if (!expandedWalletDetails[walletAccountId]) {
        await fetchWalletDetails(walletAccountId);
      }
      setExpandedWalletId(walletAccountId);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load wallet details");
    } finally {
      setLoadingWalletId(null);
    }
  };

  const handleWalletSelect = async (row, checked) => {
    try {
      if (!checked) {
        setSelectedWalletId(null);
        setSelectedWallet(null);
        return;
      }

      setSelectedWalletId(row.WalletAccountId);

      if (expandedWalletDetails[row.WalletAccountId]) {
        setSelectedWallet(expandedWalletDetails[row.WalletAccountId]);
        return;
      }

      const { wallet } = await fetchWalletDetails(row.WalletAccountId);
      setSelectedWallet(wallet);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to select wallet");
    }
  };

  const handleInternalRecharge = async () => {
    if (!selectedWallet?.WalletAccountId) {
      setError("Select a wallet first");
      return;
    }

    if (!recharge.amount || Number(recharge.amount) <= 0) {
      setError("Enter a valid amount");
      return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/wallet/admin/recharge`,
        {
          walletAccountId: selectedWallet.WalletAccountId,
          amount: Number(recharge.amount),
          paymentMode: recharge.paymentMode,
          remarks: recharge.remarks,
        },
        { headers: getAuthHeaders() }
      );

      setSuccess(
        `Recharge successful. Bonus ₹${Number(
          res.data?.bonusAmount || 0
        ).toFixed(2)} | Total Credit ₹${Number(
          res.data?.totalCreditAmount || 0
        ).toFixed(2)}`
      );

      setRecharge({
        amount: "",
        paymentMode: "INTERNAL",
        remarks: "",
      });

      await fetchWalletDetails(selectedWallet.WalletAccountId);
      await fetchAccounts();
    } catch (err) {
      setError(err?.response?.data?.message || "Recharge failed");
    }
  };

  const handleGatewayRecharge = async () => {
  try {
    setError("");
    setPayMsg(null);

    if (!selectedWallet?.WalletAccountId) {
      setError("Select a wallet first");
      return;
    }

    if (!recharge.amount || Number(recharge.amount) <= 0) {
      setError("Enter a valid amount");
      return;
    }

    // non-online modes go through direct/internal recharge route
    if (recharge.paymentMode !== "ONLINE") {
      await handleInternalRecharge();
      return;
    }

    setPayBusy(true);
    setPayMsg({
      type: "info",
      text: `Starting online wallet recharge for ₹${Number(recharge.amount).toFixed(2)}...`,
    });

    const idem = `WALLET_${selectedWallet.WalletAccountId}_${Date.now()}`;

    const { data } = await axios.post(
      `${API_URL}/api/wallet/admin/recharge/create-order`,
      {
        walletAccountId: Number(selectedWallet.WalletAccountId),
        amount: Number(recharge.amount),
        remarks: recharge.remarks || null,
        idempotencyKey: idem,
        mode: "LIVE",
      },
      { headers: getAuthHeaders() }
    );

    if (data.Gateway === "RAZORPAY") {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) throw new Error("Failed to load Razorpay");

      const rzp = new window.Razorpay({
        key: data.RazorpayKeyId,
        amount: Math.round(Number(data.Amount) * 100),
        currency: data.Currency || "INR",
        name: `${campusCode || "Campus"} Wallet Recharge`,
        description: data.ClientTxnRef || `Wallet ${selectedWallet.WalletAccountId}`,
        order_id: data.RazorpayOrderId,
        handler: async (resp) => {
          try {
            await axios.post(
              `${API_URL}/api/wallet/admin/recharge/confirm`,
              {
                rechargeOrderId: data.RechargeOrderId,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { headers: getAuthHeaders() }
            );

            setPayMsg({
              type: "success",
              text: "Payment received. Wallet balance is being updated...",
            });

            setRecharge({
              amount: "",
              paymentMode: "ONLINE",
              remarks: "",
            });

            await fetchWalletDetails(selectedWallet.WalletAccountId);
            await fetchAccounts();
            setSuccess("Wallet recharge completed successfully");
          } catch (e) {
            console.error(e);
            setPayMsg({
              type: "error",
              text: e?.response?.data?.message || "Payment confirmed but wallet update failed.",
            });
          }
        },
        modal: {
          ondismiss: () => {
            setPayMsg({ type: "warning", text: "Payment window closed." });
          },
        },
      });

      rzp.open();
      return;
    }

    if (data.Gateway === "PAYTM") {
      const ok = await loadScript(
        "https://securegw-stage.paytm.in/merchantpgpui/checkoutjs/merchants/" + data.PaytmMid + ".js"
      );
      if (!ok) throw new Error("Failed to load Paytm CheckoutJS");

      const config = {
        root: "",
        flow: "DEFAULT",
        data: {
          orderId: data.PaytmOrderId,
          token: data.PaytmTxnToken,
          tokenType: "TXN_TOKEN",
          amount: String(data.Amount),
        },
        handler: {
          notifyMerchant: function (eventName) {
            if (eventName === "SESSION_EXPIRED") {
              setPayMsg({ type: "error", text: "Paytm session expired. Retry." });
            }
          },
        },
      };

      setPayMsg({ type: "info", text: "Opening Paytm..." });
      await window.Paytm.CheckoutJS.init(config);
      window.Paytm.CheckoutJS.invoke();
      return;
    }

    if (data.Gateway === "BILLDESK") {
      if (data.Redirect?.gatewayUrl && data.Redirect?.formFields) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.Redirect.gatewayUrl;

        Object.entries(data.Redirect.formFields).forEach(([key, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        return;
      }

      throw new Error("Invalid BillDesk redirect payload");
    }

    throw new Error("Unsupported gateway from server");
  } catch (err) {
    console.error(err);
    setPayMsg(null);
    setError(err?.response?.data?.message || err.message || "Payment failed");
  } finally {
    setPayBusy(false);
  }
};

  const pageCountText = useMemo(() => {
    if (!total) return "0 records";
    const from = (filters.page - 1) * filters.pageSize + 1;
    const to = Math.min(filters.page * filters.pageSize, total);
    return `${from}-${to} of ${total}`;
  }, [filters.page, filters.pageSize, total]);

  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize));

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Campus Wallet Management
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Search, select, expand, and recharge wallet accounts
            </Typography>
          </Box>

        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          {!!payMsg && (
            <Alert severity={payMsg.type} onClose={() => setPayMsg(null)} sx={{ mb: 2 }}>
              {payMsg.text}
            </Alert>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  name="q"
                  label="Search"
                  value={filters.q}
                  onChange={handleFilterChange}
                  placeholder="Wallet ID / Student ID / Employee ID / Name"
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="subCampusCode"
                  label="SubCampus"
                  value={filters.subCampusCode}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                  disabled={loadingLookups}
                >
                  <MenuItem value="">All</MenuItem>
                  {options.SubCampusCode.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="cardType"
                  label="Card Type"
                  value={filters.cardType}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="STUDENT">STUDENT</MenuItem>
                  <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  name="walletStatus"
                  label="Wallet Status"
                  value={filters.walletStatus}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="BLOCKED">BLOCKED</MenuItem>
                  <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                  <MenuItem value="CLOSED">CLOSED</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={1}>
                <TextField
                  select
                  name="pageSize"
                  label="Rows"
                  value={filters.pageSize}
                  onChange={handlePageSizeChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SearchIcon />}
                    sx={primaryBtnSx}
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    sx={outlinedBtnSx}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper elevation={0} sx={tableWrapSx}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderBottom: "1px solid rgba(25,118,210,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    Wallet Accounts ({pageCountText})
                  </Typography>

                  <Pagination
                    count={totalPages}
                    page={filters.page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Box>

                <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                  <TableContainer component="div">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>Select</TableCell>
                          <TableCell>#</TableCell>
                          <TableCell>Person</TableCell>
                          <TableCell>SubCampus</TableCell>
                          <TableCell>Card Type</TableCell>
                          <TableCell>Balance</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rows.map((row, idx) => {
                          const rowNo = (filters.page - 1) * filters.pageSize + idx + 1;
                          const isExpanded = Number(expandedWalletId) === Number(row.WalletAccountId);
                          const isSelected = Number(selectedWalletId) === Number(row.WalletAccountId);
                          const detailWallet = expandedWalletDetails[row.WalletAccountId];
                          const detailLedger = expandedWalletLedger[row.WalletAccountId] || [];

                          return (
                            <React.Fragment key={row.WalletAccountId}>
                              <TableRow
                                sx={{
                                  ...zebraRowSx(idx),
                                  ...(isSelected
                                    ? { outline: "2px solid rgba(240,120,40,0.35)" }
                                    : {}),
                                }}
                                onClick={() => toggleExpandRow(row.WalletAccountId)}
                              >
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={isSelected}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => handleWalletSelect(row, e.target.checked)}
                                    sx={checkboxSx}
                                  />
                                </TableCell>
                                <TableCell>{rowNo}</TableCell>
                                <TableCell>
                                  {row.CardType === "STUDENT"
                                    ? `${row.StudentName || "-"}${row.StudentID ? ` (${row.StudentID})` : ""}`
                                    : `${row.EmployeeName || "-"}${row.EmployeeID ? ` (${row.EmployeeID})` : ""}`}
                                </TableCell>
                                <TableCell>{row.SubCampusCode || "-"}</TableCell>
                                <TableCell>{row.CardType}</TableCell>
                                <TableCell>₹{Number(row.CurrentBalance || 0).toFixed(2)}</TableCell>
                                <TableCell>{row.WalletStatus}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell colSpan={8} sx={{ p: 0, borderBottom: "none" }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ p: 1.5, backgroundColor: "rgba(25,118,210,0.03)" }}>
                                      {loadingWalletId === row.WalletAccountId ? (
                                        <Typography sx={{ opacity: 0.75 }}>
                                          Loading wallet details...
                                        </Typography>
                                      ) : (
                                        <Stack spacing={1.5}>
                                          <Paper elevation={0} sx={detailPaperSx}>
                                            <Typography sx={{ fontWeight: 900, mb: 1 }}>
                                              Wallet Details
                                            </Typography>
                                            <Grid container spacing={1}>
                                              
                                              <Grid item xs={12} md={4}>
                                                <Typography><b>Card Serial:</b> {detailWallet?.CardSerialNo || row.CardSerialNo || "-"}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={4}>
                                                <Typography><b>Card Status:</b> {detailWallet?.CardStatus || row.CardStatus || "-"}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={4}>
                                                <Typography><b>Wallet Status:</b> {detailWallet?.WalletStatus || row.WalletStatus}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={4}>
                                                <Typography><b>SubCampus:</b> {detailWallet?.SubCampusCode || row.SubCampusCode || "-"}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={4}>
                                                <Typography><b>Balance:</b> ₹{Number(detailWallet?.CurrentBalance || row.CurrentBalance || 0).toFixed(2)}</Typography>
                                              </Grid>
                                            </Grid>
                                          </Paper>

                                          <Paper elevation={0} sx={detailPaperSx}>
                                            <Typography sx={{ fontWeight: 900, mb: 1 }}>
                                              Recent Ledger Transactions
                                            </Typography>

                                            {!detailLedger.length ? (
                                              <Typography sx={{ opacity: 0.75 }}>
                                                No ledger entries found.
                                              </Typography>
                                            ) : (
                                              <Stack spacing={1}>
                                                {detailLedger.slice(0, 10).map((l) => (
                                                  <Paper
                                                    key={l.LedgerId}
                                                    elevation={0}
                                                    sx={{
                                                      p: 1.25,
                                                      borderRadius: 2,
                                                      border: "1px solid rgba(25,118,210,0.15)",
                                                      backgroundColor:
                                                        l.Direction === "CR"
                                                          ? "rgba(76,175,80,0.06)"
                                                          : "rgba(244,67,54,0.04)",
                                                    }}
                                                  >
                                                    <Typography sx={{ fontWeight: 800 }}>
                                                      {l.TxnType} | {l.Direction} | ₹{Number(l.Amount || 0).toFixed(2)}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                      Balance After: ₹{Number(l.BalanceAfter || 0).toFixed(2)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                      {formatLocalDateTime(l.CreatedAt)}
                                                    </Typography>
                                                    {l.Remarks ? (
                                                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                                        {l.Remarks}
                                                      </Typography>
                                                    ) : null}
                                                  </Paper>
                                                ))}
                                              </Stack>
                                            )}
                                          </Paper>
                                        </Stack>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}

                        {!rows.length && (
                          <TableRow sx={zebraRowSx(0)}>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              No wallet accounts found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderTop: "1px solid rgba(25,118,210,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {pageCountText}
                  </Typography>

                  <Pagination
                    count={totalPages}
                    page={filters.page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
                  Add Balance
                </Typography>

                <Stack spacing={2}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {selectedWallet
                      ? `Selected Person: ${selectedWallet.StudentID} | ${selectedWallet.CardType} | ${selectedWallet.SubCampusCode || "-"}`
                      : "Select a wallet using the checkbox in the table."}
                  </Alert>

                  <TextField
                    label="Amount"
                    type="number"
                    value={recharge.amount}
                    onChange={(e) => setRecharge((s) => ({ ...s, amount: e.target.value }))}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  />

                  <TextField
                    select
                    label="Payment Mode"
                    value={recharge.paymentMode}
                    onChange={(e) => setRecharge((s) => ({ ...s, paymentMode: e.target.value }))}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  >
                    <MenuItem value="CASH">CASH</MenuItem>
                    <MenuItem value="ONLINE">ONLINE</MenuItem>
                  </TextField>

                  <TextField
                    label="Remarks"
                    value={recharge.remarks}
                    onChange={(e) => setRecharge((s) => ({ ...s, remarks: e.target.value }))}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={inputSx}
                  />

                  <Button
                    variant="contained"
                    startIcon={<AddCardIcon />}
                    sx={primaryBtnSx}
                    onClick={handleGatewayRecharge}
                    disabled={!selectedWallet || payBusy}
                  >
                    {payBusy ? "Starting..." : "Add Balance"}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletAdminPage;